import { supabase } from "@/integrations/supabase/client";

export interface GenerateContentOptions {
  tone?: 'professional' | 'friendly' | 'luxury' | 'casual';
  include_keywords?: boolean;
}

export interface GenerateContentResult {
  success: boolean;
  content?: string;
  error?: string;
}

export interface BulkOptimizeResult {
  success: boolean;
  results?: Array<{
    product_id: string;
    product_name: string;
    before: string;
    after: string;
    success: boolean;
    error?: string;
  }>;
  total?: number;
  successful?: number;
  error?: string;
}

export interface ChatResponse {
  success: boolean;
  content?: string;
  actions?: Array<{ label: string; type: string }>;
  actionResult?: {
    toolName: string | null;
    success: boolean;
    data?: any;
    error?: string;
  };
  generatedTitle?: string | null;
  error?: string;
  // Analytics-specific fields
  chartType?: "bar" | "line" | "pie" | "table" | null;
  chartData?: Array<{ label: string; value: number }>;
  breakdown?: Array<{ label: string; value: any }>;
  analyticsContext?: {
    ordersAnalyzed: number;
    dateRange: { from: string; to: string };
    codeExecuted?: boolean;
  };
}

export interface ProductData {
  name: string;
  description?: string;
  short_description?: string;
  categories?: string[];
  price?: string;
}

export interface ImageAttachment {
  base64: string;
  filename: string;
  mimeType: string;
}

export const aiService = {
  /**
   * Generate content for a single product field (existing product)
   */
  generateContent: async (
    productId: string,
    fieldType: 'meta_title' | 'meta_description' | 'description' | 'short_description',
    options: GenerateContentOptions = {}
  ): Promise<GenerateContentResult> => {
    try {
      const { data, error } = await supabase.functions.invoke('ai-generate-content', {
        body: {
          product_id: productId,
          field_type: fieldType,
          options,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      return data as GenerateContentResult;
    } catch (error) {
      console.error('AI generate content error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate content',
      };
    }
  },

  /**
   * Generate content for a new product (not yet saved)
   */
  generateContentForNewProduct: async (
    productData: ProductData,
    fieldType: 'meta_title' | 'meta_description' | 'description' | 'short_description',
    options: GenerateContentOptions = {}
  ): Promise<GenerateContentResult> => {
    try {
      const { data, error } = await supabase.functions.invoke('ai-generate-content', {
        body: {
          product_data: productData,
          field_type: fieldType,
          options,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      return data as GenerateContentResult;
    } catch (error) {
      console.error('AI generate content for new product error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate content',
      };
    }
  },

  /**
   * Bulk optimize multiple products
   */
  bulkOptimize: async (
    productIds: string[],
    fieldType: 'meta_title' | 'meta_description' | 'description' | 'short_description',
    options: GenerateContentOptions = {}
  ): Promise<BulkOptimizeResult> => {
    try {
      const { data, error } = await supabase.functions.invoke('ai-bulk-optimize', {
        body: {
          product_ids: productIds,
          field_type: fieldType,
          options,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      return data as BulkOptimizeResult;
    } catch (error) {
      console.error('AI bulk optimize error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to bulk optimize',
      };
    }
  },

  /**
   * Send a chat message and get AI response
   */
  chat: async (
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    context: {
      currentPage?: string;
      storeId?: string;
      hasStores?: boolean;
    } = {},
    images?: ImageAttachment[],
    conversationId?: string,
    shouldGenerateTitle?: boolean
  ): Promise<ChatResponse> => {
    try {
      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: {
          messages,
          context,
          images,
          conversationId,
          shouldGenerateTitle,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      return data as ChatResponse;
    } catch (error) {
      console.error('AI chat error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get response',
      };
    }
  },

  /**
   * Send an analytics question and get AI response with visualizations
   * Uses the code interpreter for dynamic analysis
   */
  analyticsChat: async (
    question: string,
    storeId: string
  ): Promise<ChatResponse> => {
    try {
      const { data, error } = await supabase.functions.invoke('ai-analytics-interpreter', {
        body: {
          store_id: storeId,
          question,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.error) {
        throw new Error(data.message || data.error);
      }

      // Map the interpreter response to ChatResponse format
      return {
        success: true,
        content: data.answer,
        chartType: data.chartType,
        chartData: data.chartData,
        breakdown: data.breakdown,
        analyticsContext: data.context,
      };
    } catch (error) {
      console.error('AI analytics chat error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to analyze data',
      };
    }
  },
};
